import React, { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Bot, Send, Plus, Sparkles, Trash2 } from "lucide-react";

export default function DashboardChat() {
  const [sessions, setSessions] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  const loadSessions = async () => {
    try {
      const { data } = await api.get("/chat/sessions");
      setSessions(data);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    loadSessions();
  }, []);

  useEffect(() => {
    if (!activeId) {
      setMessages([]);
      return;
    }
    (async () => {
      try {
        const { data } = await api.get(`/chat/sessions/${activeId}`);
        setMessages(data.messages);
      } catch (e) {
        console.error(e);
      }
    })();
  }, [activeId]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const send = async (e) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || loading) return;
    setInput("");
    const optimistic = { role: "user", content: text };
    setMessages((m) => [...m, optimistic]);
    setLoading(true);
    try {
      const { data } = await api.post("/chat/send", { message: text, session_id: activeId });
      if (!activeId) {
        setActiveId(data.session_id);
        await loadSessions();
      }
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch (err) {
      setMessages((m) => [...m, { role: "assistant", content: `[ error · ${formatApiError(err.response?.data?.detail) || err.message} ]` }]);
    } finally {
      setLoading(false);
    }
  };

  const newChat = () => {
    setActiveId(null);
    setMessages([]);
  };

  const deleteSession = async (id, e) => {
    e.stopPropagation();
    try {
      await api.delete(`/chat/sessions/${id}`);
      if (activeId === id) {
        setActiveId(null);
        setMessages([]);
      }
      await loadSessions();
    } catch (err) {
      console.error(err);
    }
  };

  const SUGGESTIONS = [
    "What's the biggest storm risk to my crews this week?",
    "Draft an estimate for a roof tarp + interior water mitigation job.",
    "How many crews would I need if this hurricane track holds?",
    "Summarise the top 3 jobs at risk of going over budget.",
  ];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-4 h-[calc(100vh-128px)]" data-testid="chat-page">
      {/* Sidebar of sessions */}
      <aside className="bg-[#121214] border border-white/5 flex flex-col">
        <div className="p-4 border-b border-white/5">
          <button
            onClick={newChat}
            className="w-full flex items-center justify-center gap-2 btn-primary-apex py-2.5 font-mono-apex text-[10px] uppercase tracking-[0.25em]"
            data-testid="chat-new-session-btn"
          >
            <Plus size={14} /> New conversation
          </button>
        </div>
        <div className="flex-1 overflow-y-auto scroll-thin">
          <div className="font-mono-apex text-[9px] uppercase tracking-[0.3em] text-zinc-600 px-4 py-3">history</div>
          {sessions.length === 0 && (
            <div className="px-4 font-mono-apex text-[10px] text-zinc-700">No conversations yet.</div>
          )}
          {sessions.map((s) => (
            <div
              key={s.session_id}
              onClick={() => setActiveId(s.session_id)}
              className={`group px-4 py-3 cursor-pointer border-l-2 ${
                activeId === s.session_id
                  ? "bg-white/[0.03] border-[#dc2626]"
                  : "border-transparent hover:bg-white/[0.02]"
              }`}
              data-testid="chat-session-item"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="font-body text-xs text-white truncate">{s.title}</div>
                <button onClick={(e) => deleteSession(s.session_id, e)} className="text-zinc-600 hover:text-[#dc2626] opacity-0 group-hover:opacity-100">
                  <Trash2 size={12} />
                </button>
              </div>
              <div className="font-mono-apex text-[9px] uppercase tracking-[0.2em] text-zinc-600 mt-1">
                {new Date(s.updated_at).toLocaleString()}
              </div>
            </div>
          ))}
        </div>
      </aside>

      {/* Chat area */}
      <section className="bg-[#121214] border border-white/5 flex flex-col">
        <div className="p-5 border-b border-white/5 flex items-center gap-3">
          <div className="w-9 h-9 bg-[#dc2626]/15 border border-[#dc2626]/40 flex items-center justify-center">
            <Bot size={16} className="text-[#dc2626]" />
          </div>
          <div>
            <div className="font-display text-lg text-white leading-none">Apex Sentinel</div>
            <div className="font-mono-apex text-[10px] uppercase tracking-[0.25em] text-zinc-500 mt-1">
              claude · sonnet 4.5 · context-aware
            </div>
          </div>
        </div>

        <div ref={scrollRef} className="flex-1 overflow-y-auto p-6 space-y-5 scroll-thin">
          {messages.length === 0 && (
            <div className="h-full flex flex-col items-center justify-center text-center">
              <div className="w-12 h-12 bg-[#dc2626]/15 border border-[#dc2626]/30 flex items-center justify-center mb-5">
                <Sparkles size={20} className="text-[#dc2626]" />
              </div>
              <h2 className="font-display text-3xl text-white mb-2">Ready to reason.</h2>
              <p className="font-body text-sm text-zinc-400 mb-8 max-w-md">
                Ask about storm risk, crew capacity, claims, customer health, or anything that needs
                a second opinion with conviction.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-w-2xl w-full">
                {SUGGESTIONS.map((s, i) => (
                  <button
                    key={i}
                    onClick={() => setInput(s)}
                    className="text-left px-4 py-3 border border-white/5 bg-[#0a0a0b] hover:border-white/15 transition-colors font-body text-sm text-zinc-300"
                    data-testid={`chat-suggestion-${i}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div
                className={`max-w-[80%] px-5 py-3.5 ${
                  m.role === "user"
                    ? "bg-[#dc2626] text-white"
                    : "bg-[#0a0a0b] border border-white/10 text-zinc-100"
                } font-${m.role === "assistant" ? "mono-apex text-[12px] leading-relaxed" : "body text-sm"}`}
              >
                <div className="whitespace-pre-wrap">{m.content}</div>
              </div>
            </div>
          ))}
          {loading && (
            <div className="flex justify-start">
              <div className="px-5 py-3.5 bg-[#0a0a0b] border border-white/10 font-mono-apex text-xs text-zinc-400 flex items-center gap-2">
                <Sparkles size={12} className="animate-pulse text-[#dc2626]" /> reasoning…
              </div>
            </div>
          )}
        </div>

        <form onSubmit={send} className="border-t border-white/5 p-4 flex items-center gap-2">
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask Sentinel anything…"
            className="input-apex flex-1 px-4 py-3.5 font-body text-sm"
            data-testid="chat-input"
          />
          <button
            type="submit"
            disabled={loading || !input.trim()}
            className="btn-primary-apex px-5 py-3.5 disabled:opacity-50 flex items-center gap-2 font-mono-apex text-[10px] uppercase tracking-[0.25em]"
            data-testid="chat-send-btn"
          >
            <Send size={14} /> Send
          </button>
        </form>
      </section>
    </div>
  );
}
